import React, { useEffect, useState } from "react";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";

const BASE_URL = "https://api.dreamtoday.in";

const AdminBonus = () => {
  const [awards, setAwards] = useState([]);
  const [amount, setAmount] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 🔹 Fetch Awards
  const fetchAwards = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/api/admin/get-awards`);
      const data = await res.json();

      if (data.success) {
        setAwards(data.awards || []);
      } else {
        setError("Failed to fetch awards");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAwards();
  }, []);

  // 🔹 Create Award
  const handleCreate = async () => {
    if (!amount) return setError("Enter amount");

    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/api/admin/create-award`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: Number(amount) }),
      });

      const data = await res.json();

      if (data.success) {
        setAmount("");
        fetchAwards();
      } else {
        setError(data.message || "Create failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Delete Award
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this award?")) return;

    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/api/admin/delete-award/${id}`, {
        method: "DELETE",
      });

      const data = await res.json();

      if (data.success) {
        fetchAwards();
      } else {
        setError("Delete failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Update Award
  const handleUpdate = async (id) => {
    if (!editAmount) return setError("Enter amount");

    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/api/admin/update-award/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: Number(editAmount) }),
      });

      const data = await res.json();

      if (data.success) {
        setEditingId(null);
        fetchAwards();
      } else {
        setError("Update failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-10 bg-gradient-to-br from-yellow-50 to-white min-h-screen">
      
      {/* HEADER */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-yellow-700">Admin Bonus</h2>
        <p className="text-gray-500 text-sm">Manage bonus/awards</p>
      </div>

      {/* CREATE */}
      <div className="bg-white p-6 rounded-2xl shadow-md mb-6 border border-yellow-100">
        <h3 className="font-semibold text-lg mb-4 text-gray-700">Add Bonus</h3>

        <div className="flex gap-3 flex-col sm:flex-row">
          <input
            type="number"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1 px-4 py-3 rounded-xl border border-yellow-200 outline-none focus:border-yellow-400"
          />

          <button
            onClick={handleCreate}
            disabled={loading}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-600 text-white font-semibold flex items-center justify-center gap-2"
          >
            <FaPlus /> Add
          </button>
        </div>
      </div>

      {/* ERROR */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* LIST */}
      <div className="bg-white rounded-2xl shadow-md border border-yellow-100 overflow-hidden">
        <div className="p-4 border-b text-gray-600 font-semibold">
          Bonus List
        </div>

        {loading ? (
          <div className="p-6 text-center text-gray-400">Loading...</div>
        ) : awards.length === 0 ? (
          <div className="p-6 text-center text-gray-400">No data found</div>
        ) : (
          <div className="divide-y">
            {awards.map((item) => (
              <div
                key={item._id}
                className="flex items-center justify-between p-4 hover:bg-yellow-50 transition"
              >
                {/* LEFT */}
                <div>
                  {editingId === item._id ? (
                    <input
                      type="number"
                      value={editAmount}
                      onChange={(e) => setEditAmount(e.target.value)}
                      className="px-3 py-2 border border-yellow-300 rounded-lg"
                    />
                  ) : (
                    <p className="text-lg font-semibold text-gray-800">
                      ₹ {item.amount}
                    </p>
                  )}
                </div>

                {/* RIGHT */}
                <div className="flex gap-3">
                  {editingId === item._id ? (
                    <>
                      <button
                        onClick={() => handleUpdate(item._id)}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-4 py-2 bg-gray-300 rounded-lg text-sm"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setEditingId(item._id);
                          setEditAmount(item.amount);
                        }}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                      >
                        <FaEdit />
                      </button>

                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                      >
                        <FaTrash />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBonus;